import { Toggle } from '@saay/shadcn/components/toggle';
import { cn } from '@saay/shadcn/lib/utils';
import { type ComponentProps, type ReactNode, useRef } from 'react';

import { useScrollEdges } from './scroll-edges.js';

/**
 * One horizontally scrolling line of filter chips (owner, 2026-10-20: phone filters sit in a tidy
 * slider instead of wrapping rows). It snaps chip by chip, hides its scrollbar, and fades an edge
 * only while more chips are hidden behind it. Holds `Chip`s and `ToggleGroup`s of chips.
 */
export function ChipSlider({
  label,
  children,
  className,
}: {
  readonly label: string;
  readonly children: ReactNode;
  readonly className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const edges = useScrollEdges(ref);

  return (
    <div
      aria-label={label}
      className={cn('chip-slider', className)}
      data-fade-end={edges.end ? '' : undefined}
      data-fade-start={edges.start ? '' : undefined}
      ref={ref}
      role="group"
    >
      {children}
    </div>
  );
}

/**
 * A pill filter chip: a shadcn `Toggle` (a pressed-state button), muted when off and primary when
 * on. Works alone (`pressed` + `onPressedChange`) or as an item of a `ToggleGroup` (`value`). An
 * optional `count` shows as a small badge after the label.
 */
export function Chip({
  className,
  count,
  children,
  ...props
}: ComponentProps<typeof Toggle> & { readonly count?: ReactNode }) {
  return (
    <Toggle
      className={cn(
        'h-9 shrink-0 gap-1.5 rounded-full bg-muted px-4 text-[13px] font-medium text-foreground hover:bg-muted/70 aria-pressed:bg-primary aria-pressed:text-primary-foreground aria-pressed:hover:bg-primary/90',
        className,
      )}
      {...props}
    >
      {children}
      {count === undefined ? null : (
        <span className="grid h-5 min-w-5 place-items-center rounded-full bg-background/80 px-1.5 text-[11px] font-bold text-foreground tabular-nums">
          {count}
        </span>
      )}
    </Toggle>
  );
}
